import { Currency } from 'constants/currencies'
import { useShippingCost } from 'hooks/useShippingFee'
import { useUserProfile } from 'hooks/useUserProfile'
import useTranslation from 'next-translate/useTranslation'
import { TradeType } from 'types'
import { Edition, Fees } from 'utils/data'
import { getDhlFee } from 'utils/fees'

export type TradeFee = { name: string; value: number; currency?: Currency }

type Payload = {
  /**
   * The value of the current trade. SHOULD BE IN USER CURRENCY.
   */
  tradeValue: number
  /**
   * The value of the current trade in GBP, used to determine the shipping cost.
   */
  tradeValueGbp?: number
  fees: Fees
  tradeType?: TradeType
  DHLInsurance?: boolean
  edition?: Edition
  country?: string
}

export const useTradeFees = ({
  tradeValue,
  tradeValueGbp,
  fees,
  tradeType,
  DHLInsurance,
  edition,
  country,
}: Payload) => {
  const { t } = useTranslation('review_trade_form')
  const { data: userProfile } = useUserProfile()

  const shippingCountry = country || userProfile?.shippingAddress?.country

  const { data: shippingCost = 0 } = useShippingCost(
    {
      countryCode: shippingCountry,
      buyingPrice: (tradeValueGbp || 0) * 100,
      medium: edition?.artworkDetails.medium,
      size: edition?.artworkDetails.size,
      shipmentCategory: edition?.shipmentCategory,
      framed: edition?.artworkDetails?.features?.framed,
      galleryName: edition?.artworkDetails?.gallery,
      shipmentWeightOverride: edition?.shipmentWeightOverride,
    },
    {
      convertToUserCurrency: true,
      enabled: Boolean(
        shippingCountry &&
          tradeValueGbp &&
          edition?.artworkDetails?.medium &&
          edition?.artworkDetails?.size &&
          edition?.shipmentCategory
      ),
    }
  )

  const dhlFee = getDhlFee(tradeValue, fees.dhlInsuranceFee)

  let tradeFees: TradeFee[] = []

  if (tradeType === 'bid' || tradeType === 'buy') {
    tradeFees = [
      {
        name: t('shipping'),
        value: shippingCost,
      },
      { name: t('processing_fee'), value: tradeValue * fees.processFee },
    ]
  } else {
    tradeFees = [
      {
        name: `${t('platform_fee')} (${fees.applicationFee * 100}%)`,
        value: -1 * tradeValue * fees.applicationFee,
      },
      {
        name: `${t('artist_royalties')} (${fees.royalitiesFee * 100}%)`,
        value: -1 * tradeValue * fees.royalitiesFee,
      },
    ]

    if (DHLInsurance) {
      tradeFees.push({
        name: t('dhl_insurance'),
        value: -1 * dhlFee,
      })
    }
  }

  return {
    tradeFees,
    total: tradeFees.reduce((prev, curr) => prev + curr.value, 0),
  }
}
